import { c as _c } from "react/compiler-runtime";
import { add, setMilliseconds, startOfHour, sub } from 'date-fns';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import { EventStatus } from '../../../../../../../typings/Event.interface';
import { EventView } from '../../../../../../../typings/View.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import LINKS from '../../../../configs/links';
import { EventListFiltersProps, EventListQueryParams } from '../../../../typings/EventList.interface';
import { deepMergeFilters, extractFromArray, toArray } from '../../../../utils';
import { RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import formatMetricsQuery from '../../../../utils/formatMetricsQuery';
import Badge from '../../../common/base/Badge';
import Button, { ButtonGroup } from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Skeleton from '../../../common/base/Skeleton';
import { StatusDot } from '../../../common/base/StatusDot';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { BulkOperationDropdown, BulkOperationFilters } from '../../../common/BulkOperations';
import EmptyState, { EmptyResults, StyledEmptyStateWrapper } from '../../../common/EmptyState';
import { date_filter_component, event_list_filters as filter_components, FilterFormValues } from '../../../common/Filters/FilterComponents';
import Filters, { FiltersContext } from '../../../common/Filters/Filters';
import ErrorBoundary from '../../../common/helpers/ErrorBoundary';
import { StyledCentered } from '../../../common/helpers/StyledCentered';
import { Div } from '../../../common/helpers/StyledUtils';
import Search from '../../../common/Search';
import TableFooter from '../../../common/Table/TableFooter';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { TableProvider } from '../../../contexts/TableContext';
import useEventAction from '../../../hooks/useEventActions';
import useEventList, { EventListQuery } from '../../../hooks/useEventList';
import useHasSlowItemRate from '../../../hooks/useHasSlowItemRate';
import { useIsScrolled } from '../../../hooks/useIsScrolled';
import useMetricsQuery from '../../../hooks/useMetricsQuery';
import { useNextRelativeDate } from '../../../hooks/useNextRelativeDate';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, ViewTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import SuggestedColumns from '../Requests/SuggestedColumns';
import { ViewsContext } from '../ViewsContext';
import EventsList from './EventList/EventList';
import EventSidebar from './EventSidebar';
import Histogram, { DateFilter } from './Histogram';
export const extractFiltersFromQuery = (parsed_query: EventListQueryParams): EventListQuery => {
  let date = extractFromArray(parsed_query.date);
  if (typeof date === 'string') {
    const {
      relative,
      min,
      max
    } = resolveRelativeDate(date);
    date = {
      relative,
      min: min?.toISOString(),
      max: max?.toISOString()
    };
  } else {
    const parsed_query_date = parsed_query.date as {
      max?: string | string[];
      min?: string | string[];
    };
    date = {
      max: extractFromArray(parsed_query_date?.max) as string,
      min: extractFromArray(parsed_query_date?.min) as string
    };
  }
  return {
    date,
    search_term: extractFromArray(parsed_query.search_term) as string,
    webhook_id: toArray(parsed_query.webhook_id || []) as string[],
    cli_user_id: parsed_query.cli_user_id === 'any' ? 'any' : toArray(parsed_query.cli_user_id ?? []) as string[],
    source_id: toArray(parsed_query.source_id ?? []) as string[],
    destination_id: toArray(parsed_query.destination_id ?? []) as string[],
    delivery_group: parsed_query.delivery_group,
    issue_id: extractFromArray(parsed_query.issue_id) as string,
    bulk_retry_id: extractFromArray(parsed_query.bulk_retry_id) as string,
    request: {
      headers: extractFromArray(parsed_query.request?.headers) as string,
      body: extractFromArray(parsed_query.request?.body) as string,
      path: extractFromArray(parsed_query.request?.path) as string,
      parsed_query: extractFromArray(parsed_query.request?.parsed_query) as string
    },
    status: toArray(parsed_query.status ?? []) as string[],
    response_status: {
      max: extractFromArray(parsed_query.response?.response_status?.max) as string,
      min: extractFromArray(parsed_query.response?.response_status?.min) as string
    },
    error_code: toArray(parsed_query.response?.error_code ?? []) as string[],
    attempts: {
      max: extractFromArray(parsed_query.attempts?.max) as string,
      min: extractFromArray(parsed_query.attempts?.min) as string
    },
    scheduled_attempts: extractFromArray(parsed_query.scheduled_attempts) as 'true' | 'false',
    next: extractFromArray(parsed_query.next) as string,
    prev: extractFromArray(parsed_query.prev) as string
  };
};
const extractFiltersFromView = (current_view: EventView): EventListFiltersProps => {
  const filters = current_view.filters;
  return {
    ...filters,
    date: {
      relative: extractFromArray(filters.date?.relative) as RelativeDate,
      max: extractFromArray(filters.date?.max) as string,
      min: extractFromArray(filters.date?.min) as string
    },
    response_status: {
      max: extractFromArray(filters.response_status?.max?.toString()) as string,
      min: extractFromArray(filters.response_status?.min?.toString()) as string
    },
    attempts: {
      min: extractFromArray(filters.attempts?.min?.toString()) as string,
      max: extractFromArray(filters.attempts?.max?.toString()) as string
    }
  };
};
const extractFiltersForList = (filters: EventListFiltersProps): EventListFiltersProps => {
  if (filters.date?.relative) {
    const {
      min,
      max
    } = resolveRelativeDate(filters.date.relative);
    return {
      ...filters,
      date: {
        min: min.toISOString(),
        max: max.toISOString()
      }
    };
  }
  return filters;
};
const DEFAULT_DATE = 'yesterday';
const MIN_SEARCH_TERM_LENGTH = 3;
const normalizeSearchTerm = (search_term?: string) => {
  if (!search_term || search_term.length < MIN_SEARCH_TERM_LENGTH) {
    return undefined;
  }
  return search_term;
};
const filters_initial_state = extractFiltersFromQuery({
  date: DEFAULT_DATE
});

// Hoisted out of EventsView: resolves a relative date filter into a concrete stats window.
// Must never mutate `filters` — writing the resolved min/max back onto filters.date gives
// every consumer of `filters` new content each render, which cascades into a formik update
// loop ("Maximum update depth exceeded") in the filters sync.
const computeStatsDateRange = (filters: EventListFiltersProps): [Date, Date] => {
  const now = new Date();
  let date_min = filters.date?.min;
  let date_max = filters.date?.max;
  if (filters.date?.relative) {
    const {
      min,
      max
    } = resolveRelativeDate(filters.date.relative);
    date_min = min.toISOString();
    date_max = max.toISOString();
  }
  const end_date = setMilliseconds(date_max ? new Date(date_max) : add(startOfHour(now), {
    hours: 1
  }), 0);
  const start_date = setMilliseconds(date_min ? new Date(date_min) : sub(add(startOfHour(now), {
    hours: 1
  }), {
    days: 1
  }), 0);
  return [start_date, end_date];
};
const EventsView: React.FC<{
  current_view?: EventView;
}> = t0 => {
  const $ = _c(190);
  const {
    current_view
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const theme = useTheme();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    has_connection
  } = useContext(DashboardContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Date.now();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [list_render_key, setRenderKey] = useState(t1);
  const [ignore_current_view, setIgnoreCurrentView] = useState(false);
  const current_page = query.current_page ? parseInt(query.current_page, 10) : 1;
  const {
    has_scrolled,
    scrolled_element_ref
  } = useIsScrolled();
  const search_input_ref = useRef(null);
  const {
    views,
    createView,
    updateView,
    deleteView,
    renameView,
    duplicateView
  } = useContext(ViewsContext);
  const is_cli_view = current_view?.id === "cli";
  let t2;
  if ($[1] !== query.selected_event_id) {
    t2 = extractFromArray(query.selected_event_id);
    $[1] = query.selected_event_id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selected_event_id = t2 as string;
  let t3;
  if ($[3] !== query.selected_attempt_id) {
    t3 = extractFromArray(query.selected_attempt_id);
    $[3] = query.selected_attempt_id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const selected_attempt_id = t3 as string;
  let new_filters;
  if ($[5] !== query) {
    new_filters = extractFiltersFromQuery(query);
    if (!query.date) {
      let t4;
      let t5;
      let t6;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        const {
          min,
          max
        } = resolveRelativeDate(DEFAULT_DATE);
        t4 = DEFAULT_DATE;
        t5 = min?.toISOString();
        t6 = max?.toISOString();
        $[7] = t4;
        $[8] = t5;
        $[9] = t6;
      } else {
        t4 = $[7];
        t5 = $[8];
        t6 = $[9];
      }
      let t7;
      if ($[10] !== t5 || $[11] !== t6) {
        t7 = {
          relative: t4,
          min: t5,
          max: t6
        };
        $[10] = t5;
        $[11] = t6;
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      new_filters.date = t7;
    }
    $[5] = query;
    $[6] = new_filters;
  } else {
    new_filters = $[6];
  }
  if (current_view && !ignore_current_view) {
    const should_use_query = Object.values(filter_components).some(v => query[v.filter_key] !== undefined);
    if (!should_use_query) {
      let t4;
      if ($[13] !== current_view) {
        t4 = extractFiltersFromView(current_view);
        $[13] = current_view;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      const view_filters = t4;
      let t5;
      if ($[15] !== new_filters.next || $[16] !== new_filters.prev || $[17] !== view_filters) {
        t5 = {
          ...filters_initial_state,
          ...view_filters,
          next: new_filters.next,
          prev: new_filters.prev
        };
        $[15] = new_filters.next;
        $[16] = new_filters.prev;
        $[17] = view_filters;
        $[18] = t5;
      } else {
        t5 = $[18];
      }
      new_filters = t5;
    }
  }
  const filters = new_filters;
  const [search_term_input, setSearchTermInput] = useState(filters.search_term || "");
  let t4;
  let t5;
  if ($[19] !== filters.search_term) {
    t4 = () => {
      setSearchTermInput(filters.search_term || "");
    };
    t5 = [filters.search_term];
    $[19] = filters.search_term;
    $[20] = t4;
    $[21] = t5;
  } else {
    t4 = $[20];
    t5 = $[21];
  }
  useEffect(t4, t5);
  const applied_filters = {
    ...filters,
    search_term: normalizeSearchTerm(filters.search_term)
  };
  const {
    next_relative_date
  } = useNextRelativeDate(filters, subscription);
  let t6;
  bb0: {
    if ("dynamic_columns" in query) {
      t6 = query.dynamic_columns === null ? undefined : query.dynamic_columns;
      break bb0;
    }
    if (current_view && !ignore_current_view && current_view.dynamic_columns) {
      t6 = current_view.dynamic_columns;
      break bb0;
    }
    t6 = undefined;
  }
  const dynamic_columns = t6;
  const t7 = filters.date?.relative;
  let t8;
  if ($[22] !== t7) {
    t8 = {
      include_data: true,
      type: "events",
      date_filter_relative: t7
    };
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const list_data = useEventList(extractFiltersForList(applied_filters), t8);
  const latest_event = list_data.events.length > 0 && list_data.events[0];
  const has_slow_event_rate = useHasSlowItemRate(list_data.events);
  const [stats_start_date, stats_end_date] = computeStatsDateRange(filters);
  const query_params = {
    filters: {
      ...applied_filters,
      date: {
        min: stats_start_date.toISOString(),
        max: stats_end_date.toISOString()
      }
    }
  };
  const metrics_query = formatMetricsQuery("Events", query_params);
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      resetResultSetOnChange: false
    };
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const {
    raw_data,
    is_loading: is_loading_metrics,
    refetch
  } = useMetricsQuery(metrics_query, t9);
  const has_request_filter = Object.values(applied_filters.request ?? {}).some(_temp) || applied_filters.search_term;
  const total_count = raw_data && raw_data.length > 0 ? raw_data[0]["Events.count"] : null;
  let t10;
  if ($[25] !== updateSearchQuery) {
    t10 = selected_event_id_0 => updateSearchQuery({
      selected_event_id: selected_event_id_0
    }, {
      remove_keys: ["selected_attempt_id"]
    });
    $[25] = updateSearchQuery;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const onEventSelected = t10;
  let t11;
  if ($[27] !== updateSearchQuery) {
    t11 = selected_attempt_id_0 => updateSearchQuery({
      selected_attempt_id: selected_attempt_id_0
    });
    $[27] = updateSearchQuery;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const onAttemptSelected = t11;
  let t12;
  if ($[29] !== list_data.events || $[30] !== onEventSelected || $[31] !== selected_event_id) {
    t12 = () => {
      if (!selected_event_id || !list_data.events.length) {
        return;
      }
      const current_index = list_data.events.findIndex(event => event.id === selected_event_id);
      if (current_index > 0) {
        onEventSelected(list_data.events[current_index - 1].id);
      }
    };
    $[29] = list_data.events;
    $[30] = onEventSelected;
    $[31] = selected_event_id;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const onPrevEvent = t12;
  let t13;
  if ($[33] !== list_data.events || $[34] !== onEventSelected || $[35] !== selected_event_id) {
    t13 = () => {
      if (!selected_event_id || !list_data.events.length) {
        return;
      }
      const current_index_0 = list_data.events.findIndex(event_0 => event_0.id === selected_event_id);
      if (current_index_0 < list_data.events.length - 1) {
        onEventSelected(list_data.events[current_index_0 + 1].id);
      }
    };
    $[33] = list_data.events;
    $[34] = onEventSelected;
    $[35] = selected_event_id;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const onNextEvent = t13;
  let t14;
  if ($[37] !== HookdeckAPI.track || $[38] !== current_view || $[39] !== dynamic_columns || $[40] !== filters.date || $[41] !== filters.search_term || $[42] !== selected_event_id || $[43] !== updateSearchQuery) {
    t14 = new_filters_0 => {
      HookdeckAPI.track.event("Filtered Event List", new_filters_0);
      if (current_view) {
        setIgnoreCurrentView(true);
      }
      updateSearchQuery({
        ...new_filters_0,
        search_term: filters.search_term,
        date: filters.date?.relative ? filters.date.relative : filters.date,
        selected_event_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["selected_attempt_id", "next", "prev", "current_page"],
        replace: true
      });
    };
    $[37] = HookdeckAPI.track;
    $[38] = current_view;
    $[39] = dynamic_columns;
    $[40] = filters.date;
    $[41] = filters.search_term;
    $[42] = selected_event_id;
    $[43] = updateSearchQuery;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const onFilterChanged = t14;
  let t15;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = search_term => {
      setSearchTermInput(search_term);
    };
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const onSearchTermInputChange = t15;
  let t16;
  if ($[46] !== current_view || $[47] !== dynamic_columns || $[48] !== filters || $[49] !== selected_event_id || $[50] !== updateSearchQuery) {
    t16 = search_term_0 => {
      if (current_view) {
        setIgnoreCurrentView(true);
      }
      search_input_ref?.current?.blur();
      updateSearchQuery({
        ...filters,
        search_term: normalizeSearchTerm(search_term_0),
        date: filters.date?.relative ? filters.date.relative : filters.date,
        selected_event_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["selected_attempt_id", "next", "prev", "current_page"],
        replace: true
      });
    };
    $[46] = current_view;
    $[47] = dynamic_columns;
    $[48] = filters;
    $[49] = selected_event_id;
    $[50] = updateSearchQuery;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const submitSearchTerm = t16;
  let t17;
  if ($[52] !== updateSearchQuery) {
    t17 = new_filters_1 => {
      const pagination = {
        next: new_filters_1.next || undefined,
        prev: new_filters_1.prev || undefined
      };
      updateSearchQuery(pagination, {
        remove_keys: ["selected_attempt_id", "selected_event_id"]
      });
    };
    $[52] = updateSearchQuery;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const onPaginationChanged = t17;
  let t18;
  if ($[54] !== filters || $[55] !== updateSearchQuery) {
    t18 = date => {
      if (!date.relative) {
        delete date.relative;
      }
      updateSearchQuery({
        ...filters,
        date: date.relative ? date.relative : date
      }, {
        remove_keys: ["next", "prev", "current_page"]
      });
    };
    $[54] = filters;
    $[55] = updateSearchQuery;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const setDate = t18;
  let t19;
  if ($[57] !== createView || $[58] !== dynamic_columns || $[59] !== filters.date) {
    t19 = values => {
      let view_filters_0 = filter_components.reduce((object, t20) => {
        const {
          form_name,
          formatForDB,
          isActive
        } = t20;
        if (!isActive(values[form_name])) {
          return object;
        }
        const formatted = formatForDB(values[form_name]);
        if (!formatted) {
          return object;
        }
        return deepMergeFilters(object, formatted);
      }, {});
      if (filters.date && date_filter_component.isActive(filters.date)) {
        const formatted_date = date_filter_component.formatForDB(filters.date);
        if (formatted_date) {
          view_filters_0 = deepMergeFilters(view_filters_0, formatted_date);
        }
      }
      createView(view_filters_0, dynamic_columns || []);
    };
    $[57] = createView;
    $[58] = dynamic_columns;
    $[59] = filters.date;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  const handleCreateViewWithValues = t19;
  let t20;
  if ($[61] !== current_view || $[62] !== dynamic_columns || $[63] !== filters.date || $[64] !== updateView) {
    t20 = values_0 => {
      let view_filters_1 = filter_components.reduce((object_0, t21) => {
        const {
          form_name: form_name_0,
          formatForDB: formatForDB_0,
          isActive: isActive_0
        } = t21;
        if (!isActive_0(values_0[form_name_0])) {
          return object_0;
        }
        const formatted_0 = formatForDB_0(values_0[form_name_0]);
        if (!formatted_0) {
          return object_0;
        }
        return deepMergeFilters(object_0, formatted_0);
      }, {});
      if (filters.date && date_filter_component.isActive(filters.date)) {
        const formatted_date_0 = date_filter_component.formatForDB(filters.date);
        if (formatted_date_0) {
          view_filters_1 = deepMergeFilters(view_filters_1, formatted_date_0);
        }
      }
      updateView(current_view.id, view_filters_1, dynamic_columns || []);
    };
    $[61] = current_view;
    $[62] = dynamic_columns;
    $[63] = filters.date;
    $[64] = updateView;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  const handleUpdateViewWithValues = t20;
  let t21;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      setIgnoreCurrentView(false);
    };
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== current_view) {
    t22 = [current_view];
    $[67] = current_view;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  useEffect(t21, t22);
  let event_2;
  let t23;
  let t24;
  if ($[69] !== list_data.events || $[70] !== selected_event_id) {
    event_2 = list_data.events && list_data.events.find(event_1 => event_1.id === selected_event_id);
    t23 = useSWR;
    t24 = event_2 && APIMethodKeys.attempts.list({
      event_id: event_2.id
    });
    $[69] = list_data.events;
    $[70] = selected_event_id;
    $[71] = event_2;
    $[72] = t23;
    $[73] = t24;
  } else {
    event_2 = $[71];
    t23 = $[72];
    t24 = $[73];
  }
  let t25;
  if ($[74] !== HookdeckAPI.attempts || $[75] !== event_2?.id) {
    t25 = () => HookdeckAPI.attempts.list({
      event_id: event_2?.id
    });
    $[74] = HookdeckAPI.attempts;
    $[75] = event_2?.id;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      refreshInterval: 5000,
      revalidateOnFocus: true
    };
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  const {
    data: event_attempts,
    mutate: mutateAttempts
  } = t23(t24, t25, t26);
  const latest_attempt = event_attempts && event_attempts.models[0];
  let t27;
  if ($[78] !== event_2 || $[79] !== latest_attempt || $[80] !== list_data) {
    t27 = () => {
      if (event_2 && latest_attempt && new Date(event_2.updated_at).getTime() < new Date(latest_attempt.created_at).getTime() && latest_attempt.event_id === event_2.id && (latest_attempt.status !== event_2.status || latest_attempt.created_at !== event_2.last_attempt_at || latest_attempt.attempt_number !== event_2.attempts || latest_attempt.response_status !== event_2.response_status || latest_attempt.error_code !== event_2.error_code)) {
        list_data.refresh({
          ...event_2,
          status: latest_attempt.status as EventStatus,
          last_attempt_at: latest_attempt.created_at,
          attempts: latest_attempt.attempt_number,
          response_status: latest_attempt.response_status,
          error_code: latest_attempt.error_code
        });
      }
    };
    $[78] = event_2;
    $[79] = latest_attempt;
    $[80] = list_data;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== latest_attempt) {
    t28 = [latest_attempt];
    $[82] = latest_attempt;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  useEffect(t27, t28);
  let t29;
  if ($[84] !== list_data || $[85] !== mutateAttempts || $[86] !== selected_event_id) {
    t29 = (action, event_3) => {
      if (event_3.id === selected_event_id) {
        mutateAttempts();
      }
      list_data.refresh(event_3);
    };
    $[84] = list_data;
    $[85] = mutateAttempts;
    $[86] = selected_event_id;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  const event_actions = useEventAction(t29);
  let t30;
  if ($[88] !== current_view || $[89] !== deleteView || $[90] !== duplicateView || $[91] !== dynamic_columns || $[92] !== filters.date || $[93] !== handleCreateViewWithValues || $[94] !== handleUpdateViewWithValues || $[95] !== renameView || $[96] !== views) {
    t30 = t31 => {
      const {
        values: values_1
      } = t31;
      const filters_active_count_from_values = filter_components.reduce((count, f) => {
        if (f.isActive(values_1[f.form_name])) {
          count++;
        }
        return count;
      }, 0);
      const has_active_filters = filters_active_count_from_values > 0 || filters.date && date_filter_component.isActive(filters.date) && filters.date.relative !== DEFAULT_DATE || dynamic_columns && dynamic_columns.length > 0;
      if (current_view?.id === "cli") {
        return <Tooltip align="right" tooltip="The default CLI view is not editable"><Button disabled={true} outline={true} icon="save">Save view</Button></Tooltip>;
      }
      if (!current_view) {
        if (views && views?.count > 249) {
          return <Tooltip align="right" tooltip="Views limit reached"><Button disabled={true} outline={true} icon="save">Save view</Button></Tooltip>;
        }
        return <Button outline={true} disabled={!has_active_filters} onClick={() => {
          handleCreateViewWithValues(values_1);
        }} icon="save">Save view</Button>;
      }
      return <ButtonGroup small={true} more_options={[{
        label: "Save as new view",
        disabled: views && views.count > 249,
        tooltip: views && views.count > 249 ? "Views limit reached" : undefined,
        onClick: () => {
          handleCreateViewWithValues(values_1);
        }
      }, {
        label: "Rename view",
        onClick: () => renameView(current_view.id)
      }, {
        label: "Duplicate view",
        onClick: () => duplicateView(current_view.id)
      }, {
        label: "Delete view",
        danger: true,
        onClick: () => {
          deleteView(current_view.id);
        }
      }]}><Button outline={true} onClick={() => {
          handleUpdateViewWithValues(values_1);
        }} disabled={!has_active_filters} icon="save">Save view</Button></ButtonGroup>;
    };
    $[88] = current_view;
    $[89] = deleteView;
    $[90] = duplicateView;
    $[91] = dynamic_columns;
    $[92] = filters.date;
    $[93] = handleCreateViewWithValues;
    $[94] = handleUpdateViewWithValues;
    $[95] = renameView;
    $[96] = views;
    $[97] = t30;
  } else {
    t30 = $[97];
  }
  const SaveViewButton = t30;
  const active_table = !(!has_connection && !list_data.events.length && list_data.fetched) && list_data.events.length > 0;
  const should_show_list_loading = list_data.is_loading && list_data.count === 0;
  let t31;
  if ($[98] !== dynamic_columns) {
    t31 = {
      dynamic_columns
    };
    $[98] = dynamic_columns;
    $[99] = t31;
  } else {
    t31 = $[99];
  }
  let t32;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    $[100] = t32;
  } else {
    t32 = $[100];
  }
  const t33 = current_view?.name ? `${current_view.name} events` : "Events";
  let t34;
  if ($[101] !== t33) {
    t34 = <ViewTitle title={t33} tooltip="Events are outbound requests to destination(s) queued from inbound HTTP requests to Hookdeck (via sources). Your connection configuration(s) determine the behavior of events en route, and when a delivery effort to a destination is made, Hookdeck records each delivery attempt." link={LINKS.product_docs.events} link_label="Events & attempts docs" />;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== has_request_filter || $[104] !== total_count) {
    t35 = !total_count && !has_request_filter ? <Skeleton variant="square" h={{
      px: 20
    }} /> : <Badge neutral={true}>{has_request_filter ? "\xD8" : <NumberFormat renderText={_temp2} displayType="text" value={total_count} thousandSeparator="," />}</Badge>;
    $[103] = has_request_filter;
    $[104] = total_count;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  let t36;
  if ($[106] !== t34 || $[107] !== t35) {
    t36 = <Div flex={t32}>{t34}{t35}</Div>;
    $[106] = t34;
    $[107] = t35;
    $[108] = t36;
  } else {
    t36 = $[108];
  }
  const t37 = applied_filters as BulkOperationFilters;
  let t38;
  if ($[109] !== t37) {
    t38 = <BulkOperationDropdown operation_type="cancel" filters={t37} />;
    $[109] = t37;
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  const t39 = applied_filters as BulkOperationFilters;
  let t40;
  if ($[111] !== t39) {
    t40 = <BulkOperationDropdown operation_type="retry" model="events" filters={t39} />;
    $[111] = t39;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== t38 || $[114] !== t40) {
    t41 = <ButtonGroup>{t38}{t40}</ButtonGroup>;
    $[113] = t38;
    $[114] = t40;
    $[115] = t41;
  } else {
    t41 = $[115];
  }
  const t42 = list_data.is_loading ? "loading" : "reload";
  const t43 = list_data.is_loading || !filters.date?.relative;
  let t44;
  if ($[116] !== is_loading_metrics || $[117] !== list_data || $[118] !== refetch || $[119] !== updateSearchQuery) {
    t44 = () => {
      updateSearchQuery({}, {
        remove_keys: ["prev", "next", "selected_event_id", "selected_attempt_id", "current_page"]
      });
      list_data.refresh();
      setRenderKey(Date.now());
      if (!is_loading_metrics) {
        refetch();
      }
    };
    $[116] = is_loading_metrics;
    $[117] = list_data;
    $[118] = refetch;
    $[119] = updateSearchQuery;
    $[120] = t44;
  } else {
    t44 = $[120];
  }
  let t45;
  if ($[121] !== filters.date?.relative || $[122] !== list_data.has_next_results) {
    t45 = list_data.has_next_results && filters.date?.relative && <StatusDot color="primary" m={{
      l: 2
    }} />;
    $[121] = filters.date?.relative;
    $[122] = list_data.has_next_results;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  let t46;
  if ($[124] !== t42 || $[125] !== t43 || $[126] !== t44 || $[127] !== t45) {
    t46 = <Button icon={t42} outline={true} disabled={t43} onClick={t44}>Refresh{t45}</Button>;
    $[124] = t42;
    $[125] = t43;
    $[126] = t44;
    $[127] = t45;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] !== SaveViewButton) {
    t47 = <FiltersContext.Consumer>{t48 => {
        const {
          values: values_2
        } = t48;
        return <SaveViewButton values={values_2} />;
      }}</FiltersContext.Consumer>;
    $[129] = SaveViewButton;
    $[130] = t47;
  } else {
    t47 = $[130];
  }
  let t48;
  if ($[131] !== t41 || $[132] !== t46 || $[133] !== t47) {
    t48 = <StyledViewNavActions>{t41}{t46}{t47}</StyledViewNavActions>;
    $[131] = t41;
    $[132] = t46;
    $[133] = t47;
    $[134] = t48;
  } else {
    t48 = $[134];
  }
  let t49;
  if ($[135] !== t36 || $[136] !== t48) {
    t49 = <StyledViewNavHeader>{t36}{t48}</StyledViewNavHeader>;
    $[135] = t36;
    $[136] = t48;
    $[137] = t49;
  } else {
    t49 = $[137];
  }
  let t50;
  let t51;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = {
      align: "flex-start",
      gap: 3
    };
    t51 = {
      x: 6,
      y: 3
    };
    $[138] = t50;
    $[139] = t51;
  } else {
    t50 = $[138];
    t51 = $[139];
  }
  let t52;
  if ($[140] !== filters || $[141] !== setDate || $[142] !== stats_end_date || $[143] !== stats_start_date) {
    t52 = <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} />;
    $[140] = filters;
    $[141] = setDate;
    $[142] = stats_end_date;
    $[143] = stats_start_date;
    $[144] = t52;
  } else {
    t52 = $[144];
  }
  let t53;
  if ($[145] !== search_term_input || $[146] !== submitSearchTerm) {
    t53 = () => submitSearchTerm(search_term_input);
    $[145] = search_term_input;
    $[146] = submitSearchTerm;
    $[147] = t53;
  } else {
    t53 = $[147];
  }
  const t54 = !!search_term_input && search_term_input === (filters.search_term || "");
  let t55;
  if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = {
      px: 250
    };
    $[148] = t55;
  } else {
    t55 = $[148];
  }
  let t56;
  if ($[149] !== search_term_input || $[150] !== submitSearchTerm || $[151] !== t53 || $[152] !== t54) {
    t56 = <Search search_ref={search_input_ref} onChange={onSearchTermInputChange} onSubmit={submitSearchTerm} onBlur={t53} value={search_term_input} submitted={t54} placeholder="Search payload fields" small={true} add_keybinds={true} min_w={t55} />;
    $[149] = search_term_input;
    $[150] = submitSearchTerm;
    $[151] = t53;
    $[152] = t54;
    $[153] = t56;
  } else {
    t56 = $[153];
  }
  let t57;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Divider vertical={true} />;
    $[154] = t57;
  } else {
    t57 = $[154];
  }
  let t58;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <Div w={100}><Filters.Form /></Div>;
    $[155] = t58;
  } else {
    t58 = $[155];
  }
  let t59;
  if ($[156] !== t52 || $[157] !== t56) {
    t59 = <StyledViewNavSecondary p={0}><Div flex={t50} p={t51}>{t52}{t56}{t57}{t58}</Div></StyledViewNavSecondary>;
    $[156] = t52;
    $[157] = t56;
    $[158] = t59;
  } else {
    t59 = $[158];
  }
  let t60;
  if ($[159] !== t49 || $[160] !== t59) {
    t60 = <StyledViewNav>{t49}{t59}</StyledViewNav>;
    $[159] = t49;
    $[160] = t59;
    $[161] = t60;
  } else {
    t60 = $[161];
  }
  const t61 = active_table ? "calc(100vh - 159px)" : "calc(100vh - 106px)";
  let t62;
  if ($[162] !== t61) {
    t62 = {
      overflow: "auto",
      height: t61,
      position: "relative"
    };
    $[162] = t61;
    $[163] = t62;
  } else {
    t62 = $[163];
  }
  let t63;
  if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = {
      direction: "column"
    };
    $[164] = t63;
  } else {
    t63 = $[164];
  }
  let t64;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = {
      x: 6,
      y: 3
    };
    $[165] = t64;
  } else {
    t64 = $[165];
  }
  let t65;
  if ($[166] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <Divider />;
    $[166] = t65;
  } else {
    t65 = $[166];
  }
  let t66;
  if ($[167] !== list_data.events) {
    t66 = list_data.events.length > 0 && <><Div flex={{
        align: "flex-start",
        gap: 3
      }} p={{
        x: 6,
        y: 3
      }}><Div flex={{
          align: "center",
          gap: 2
        }} h={{
          px: 28
        }} m={{
          r: 4.5
        }}><Icon icon="add_column_right" muted={true} /><Text semi={true} muted={true} text_wrap={false}>Suggested columns</Text></Div><Divider vertical={true} /><Div w={100}><SuggestedColumns entity="events" models={list_data.events} /></Div></Div><Divider /></>;
    $[167] = list_data.events;
    $[168] = t66;
  } else {
    t66 = $[168];
  }
  let t67;
  if ($[169] !== current_page || $[170] !== has_request_filter || $[171] !== list_data.count || $[172] !== list_data.events.length || $[173] !== list_data.fetched || $[174] !== list_data.is_loading || $[175] !== list_data.next || $[176] !== list_data.prev || $[177] !== total_count || $[178] !== updateSearchQuery) {
    t67 = (list_data.events.length > 0 || !list_data.fetched) && <TableFooter fill={true} current_count={list_data.count} total_count={has_request_filter ? undefined : total_count} loading={list_data.is_loading} pagination={{
      current_page,
      page_size: 100,
      entity_name: "events"
    }} onPreviousPage={current_page > 1 ? () => {
      updateSearchQuery({
        prev: list_data.prev || undefined,
        next: undefined,
        current_page: String(current_page - 1)
      });
    } : undefined} onNextPage={list_data.next ? () => {
      updateSearchQuery({
        next: list_data.next,
        prev: undefined,
        current_page: String(current_page + 1)
      });
    } : undefined} />;
    $[169] = current_page;
    $[170] = has_request_filter;
    $[171] = list_data.count;
    $[172] = list_data.events.length;
    $[173] = list_data.fetched;
    $[174] = list_data.is_loading;
    $[175] = list_data.next;
    $[176] = list_data.prev;
    $[177] = total_count;
    $[178] = updateSearchQuery;
    $[179] = t67;
  } else {
    t67 = $[179];
  }
  let t68;
  if ($[180] !== event_2 || $[181] !== event_actions || $[182] !== latest_attempt || $[183] !== onAttemptSelected || $[184] !== onNextEvent || $[185] !== onPrevEvent || $[186] !== selected_attempt_id || $[187] !== selected_event_id || $[188] !== updateSearchQuery) {
    t68 = selected_event_id && <EventSidebar event_id={selected_event_id} attempt_id={selected_attempt_id} event_actions={event_actions} list_event={event_2} list_attempt={latest_attempt && latest_attempt.id === selected_attempt_id ? latest_attempt : undefined} onSelectedAttempt={onAttemptSelected} onPrev={onPrevEvent} onNext={onNextEvent} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_event_id", "selected_attempt_id"]
    })} />;
    $[180] = event_2;
    $[181] = event_actions;
    $[182] = latest_attempt;
    $[183] = onAttemptSelected;
    $[184] = onNextEvent;
    $[185] = onPrevEvent;
    $[186] = selected_attempt_id;
    $[187] = selected_event_id;
    $[188] = updateSearchQuery;
    $[189] = t68;
  } else {
    t68 = $[189];
  }
  return <TableProvider initialConfig={t31} has_scrolled={has_scrolled}><Filters.Provider route="events" filters={filters} components={filter_components} onFilterChanged={onFilterChanged}><StyledViewLayout><StyledViewContent>{t60}<Div style={t62} flex={t63} ref={scrolled_element_ref}><StyledCentered><ErrorBoundary><Div p={t64}><Histogram dimention="status" model="Events" key={list_render_key} start_date={stats_start_date} end_date={stats_end_date} filters={applied_filters} setDate={setDate} refresh_key={latest_event ? latest_event.created_at : undefined} /></Div></ErrorBoundary>{t65}{t66}</StyledCentered>{!has_connection && !list_data?.events?.length && !should_show_list_loading ? <StyledEmptyStateWrapper><EmptyState title="Monitor outbound events" description="Events represent outbound requests to destination(s) queued from inbound HTTP requests to Hookdeck (via sources). Configure a connection to view events." asset={`events-${theme.mode}.png`} asset_position="background" cta={{
                label: "Create first connection",
                icon: "connections",
                to: "/create-first-connection"
              }} /></StyledEmptyStateWrapper> : list_data.events.length === 0 && (applied_filters.search_term || filters.status) && list_data.fetched && !should_show_list_loading ? <StyledEmptyStateWrapper><EmptyResults label="No matching events found" cta={next_relative_date ? <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} /> : undefined} /></StyledEmptyStateWrapper> : <ErrorBoundary><EventsList {...list_data} fetched={should_show_list_loading ? false : list_data.fetched} pagination={{
                next: list_data.next || undefined,
                prev: list_data.prev || undefined,
                page_size: 100,
                current_page
              }} layout={is_cli_view ? "cli" : "http"} filters={filters} onEventSelected={onEventSelected} onAttemptSelected={onAttemptSelected} selected_attempt_id={selected_attempt_id} selected_event_id={selected_event_id} onPaginationChanged={onPaginationChanged} total_count={total_count} event_actions={event_actions} event_attempts={event_attempts} has_new_results={list_data.has_next_results && has_slow_event_rate} onRefresh={() => {
                updateSearchQuery({}, {
                  remove_keys: ["prev", "next", "selected_event_id", "selected_attempt_id", "current_page"]
                });
                list_data.refresh();
                setRenderKey(Date.now());
                if (!is_loading_metrics) {
                  refetch();
                }
              }} /></ErrorBoundary>}</Div>{t67}</StyledViewContent>{t68}</StyledViewLayout></Filters.Provider></TableProvider>;
};
export default EventsView;
function _temp(v_0) {
  return !!v_0;
}
function _temp2(v_1) {
  return v_1;
}